import React, { useState } from 'react';
import { Flag, X, AlertTriangle, Shield } from 'lucide-react';
import { moderationService } from '../../services/moderationService';

interface ReportContentProps {
  postId: string;
  reporterId?: string;
  onReportSubmitted?: () => void;
}

const ReportContent: React.FC<ReportContentProps> = ({
  postId,
  reporterId,
  onReportSubmitted
}) => {
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    reason: '',
    description: '',
    reporterEmail: ''
  });

  const reportReasons = [
    { value: 'spam', label: 'Spam' },
    { value: 'inappropriate_content', label: 'Inappropriate Content' },
    { value: 'hate_speech', label: 'Hate Speech' },
    { value: 'harassment', label: 'Harassment' },
    { value: 'copyright_violation', label: 'Copyright Violation' },
    { value: 'misinformation', label: 'Misinformation' },
    { value: 'violence', label: 'Violence' },
    { value: 'self_harm', label: 'Self Harm' },
    { value: 'child_safety', label: 'Child Safety' },
    { value: 'other', label: 'Other' }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.reason || !formData.description || !formData.reporterEmail) {
      return;
    }

    try {
      setSubmitting(true);
      
      await moderationService.createReport({
        postId,
        reporterId: reporterId || 'anonymous',
        reporterEmail: formData.reporterEmail,
        reason: formData.reason as any,
        description: formData.description
      });

      setShowModal(false);
      setFormData({ reason: '', description: '', reporterEmail: '' });
      onReportSubmitted?.();
      
      // Show success message (you could add a toast notification here)
      alert('Report submitted successfully. Thank you for helping keep our community safe.');
    } catch (error) {
      console.error('Error submitting report:', error);
      alert('Failed to submit report. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
        title="Report this content"
      >
        <Flag className="w-4 h-4" />
        Report
      </button>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-red-600" />
                  <h3 className="text-xl font-semibold">Report Content</h3>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-yellow-600 mt-0.5" />
                  <div className="text-sm text-yellow-800">
                    <p className="font-medium">Before reporting, please consider:</p>
                    <ul className="mt-1 list-disc list-inside space-y-1">
                      <li>Is this content actually harmful or inappropriate?</li>
                      <li>Does it violate our community guidelines?</li>
                      <li>Are you reporting based on personal disagreement?</li>
                    </ul>
                  </div>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reason for reporting *
                  </label>
                  <select
                    name="reason"
                    value={formData.reason}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  >
                    <option value="">Select a reason...</option>
                    {reportReasons.map(reason => (
                      <option key={reason.value} value={reason.value}>
                        {reason.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Additional details *
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    required
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Please provide specific details about why this content should be reviewed..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Your email address *
                  </label>
                  <input
                    type="email"
                    name="reporterEmail"
                    value={formData.reporterEmail}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="your@email.com"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    We'll use this to follow up on your report if needed. Your email will be kept private.
                  </p>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {submitting ? 'Submitting...' : 'Submit Report'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>

              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-600">
                  <strong>Note:</strong> Reports are reviewed by our moderation team. 
                  False reports may result in account restrictions. We take all reports seriously 
                  and will investigate promptly.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ReportContent;
